/**
 * The PROFILE screen's panels (HTML), and the account readout on the main
 * menu's footer. The screen itself (events, pad, name field) is in Menu.
 */
import { shortSuiAddress } from "@act/shared";
import type { PilotState } from "../account/pilot";
import { favouriteShip, type PilotStats } from "../account/pilotStats";
import { SHIPS } from "../config/ships";
import { TRACKS } from "../config/tracks";
import { ICONS, shipIcon } from "./marks";

/** Text for innerHTML: names and emails come from outside the game. */
export function esc(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function fmtLap(ms: number): string {
  const t = ms / 1000;
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  const cs = Math.floor((t * 100) % 100);
  return `${m}:${s.toString().padStart(2, "0")}.${cs.toString().padStart(2, "0")}`;
}

function fmtDuration(ms: number): string {
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min}M ${Math.floor((ms % 60000) / 1000)}S`;
  return `${Math.floor(min / 60)}H ${min % 60}M`;
}

/** The pilot card's name field while it's open. */
export interface NameEdit {
  draft: string;
  error: string;
  saving: boolean;
}

/** Left: who's flying. The name, the ACT account (guest or Google), its Account ID and linked Sui. */
export function pilotCardHtml(state: PilotState, label: string, focus: number, edit: NameEdit | null): string {
  const online = state.status === "online";
  const view = state.account;
  const row = (k: string, jp: string, v: string, cls = "") =>
    `<div class="vd-pf-row"><span class="vd-pf-rk">${k}<i>${jp}</i></span><span class="vd-pf-rv ${cls}">${v}</span></div>`;

  let rows: string;
  if (!online) {
    const why = state.status === "connecting" ? "CONNECTING…" : "OFFLINE";
    rows = row("ACCOUNT", "アカウント", why, "dim");
  } else if (state.guest) {
    rows = row("ACCOUNT", "アカウント", "GUEST · THIS BROWSER ONLY", "dim") + row("ACCOUNT ID", "アカウントID", "SIGN IN TO GET ONE", "dim");
  } else {
    const id = view.accountId
      ? esc(shortSuiAddress(view.accountId))
      : view.accountIdWaiting === "unavailable"
        ? "COMING SOON"
        : "SIGN IN AGAIN TO MAKE IT";
    rows =
      row("ACCOUNT", "アカウント", esc(view.email ?? "GOOGLE"), "mail") +
      row("ACCOUNT ID", "アカウントID", id, view.accountId ? "addr" : "dim") +
      row("LINKED SUI", "リンク", String(view.linked.length));
  }

  const nameField = edit
    ? `<div class="vd-pf-edit">
        <input class="vd-pf-input" maxlength="24" value="${esc(edit.draft)}" spellcheck="false" autocomplete="off" aria-label="Pilot name" ${edit.saving ? "disabled" : ""} />
        <button class="vd-pf-mini save" ${edit.saving ? "disabled" : ""}>${edit.saving ? "SAVING" : "SAVE"}</button>
        <button class="vd-pf-mini cancel">CANCEL</button>
        ${edit.error ? `<span class="vd-pf-err">${esc(edit.error)}</span>` : ""}
      </div>`
    : "";

  const btn = (i: number, act: string, icon: string, name: string, jp: string) => `
    <button class="vd-pf-btn ${focus === i ? "sel" : ""} ${online ? "" : "off"}" data-act="${act}">
      <span class="vd-ic">${icon}</span>
      <span><span class="vd-pf-btn-name">${name}</span><span class="vd-pf-btn-jp">${jp}</span></span>
    </button>`;

  return `
    <section class="vd-pf-card">
      <div class="vd-pf-k">PILOT <i>パイロット</i></div>
      <div class="vd-pf-name">${esc(label)}</div>
      <span class="vd-pf-bar vd-hatch-pink"></span>
      ${nameField}
      <div class="vd-pf-rows">${rows}</div>
      <div class="vd-pf-actions">
        ${btn(0, "name", ICONS.editor, state.name ? "CHANGE NAME" : "SET NAME", "名前")}
        ${btn(1, "account", ICONS.pilot, state.guest ? "SIGN IN" : "ACCOUNT", state.guest ? "サインイン" : "アカウント")}
      </div>
      <span class="corner"></span>
    </section>`;
}

/** Right: the Vector Drift record. Counts, the craft flown most, the best lap on each course. */
export function statsHtml(stats: PilotStats): string {
  const tile = (k: string, jp: string, v: string) =>
    `<div class="vd-pf-tile"><b>${v}</b><span>${k}</span><i>${jp}</i></div>`;
  const fav = favouriteShip(stats);
  const favShip = fav ? SHIPS.find((s) => s.id === fav.id) : undefined;
  const laps = TRACKS.map((t) => {
    const best = stats.best[t.id];
    const ship = best ? SHIPS.find((s) => s.id === best.ship) : undefined;
    return `
      <div class="vd-pf-lap ${best ? "" : "none"}">
        <span class="vd-pf-lap-name">${esc(t.name)}</span>
        <span class="vd-pf-lap-ship">${ship ? ship.code : ""}</span>
        <span class="vd-pf-lap-ms">${best ? fmtLap(best.ms) : "—"}</span>
      </div>`;
  }).join("");

  return `
    <section class="vd-pf-stats">
      <div class="vd-pf-tiles">
        ${tile("RACES", "レース", String(stats.races))}
        ${tile("WINS", "優勝", String(stats.wins))}
        ${tile("PODIUMS", "表彰台", String(stats.podiums))}
        ${tile("TIME ATTACK", "タイムアタック", String(stats.timeAttack))}
        ${tile("TIME RACED", "走行時間", fmtDuration(stats.raceMs))}
        ${tile("DISTANCE", "走行距離", `${(stats.distanceM / 1000).toFixed(1)} KM`)}
      </div>
      <div class="vd-pf-fav">
        <span class="vd-pf-k">FAVOURITE CRAFT <i>愛機</i></span>
        ${
          favShip && fav
            ? `<span class="vd-pf-fav-ship"><span class="vd-pf-emblem">${shipIcon(favShip.id, favShip.color.toHexString())}</span>${favShip.code} <b>${favShip.name}</b><em>${fav.races} ${fav.races === 1 ? "RACE" : "RACES"}</em></span>`
            : `<span class="vd-pf-fav-ship dim">NO RACES YET</span>`
        }
      </div>
      <div class="vd-pf-k">BEST LAPS <i>ベストラップ</i></div>
      <div class="vd-pf-laps">${laps}</div>
    </section>`;
}

/** The main menu's footer readout: is the ACT account connected, and as what. */
export function netStatusHtml(state: PilotState): string {
  const text = { online: "ONLINE", connecting: "CONNECTING", offline: "OFFLINE", off: "OFFLINE" }[state.status];
  const kind = state.status === "online" ? `<span>ACT · ${state.guest ? "GUEST" : "SIGNED IN"}</span>` : "";
  return `<span class="vd-net ${state.status}"><span class="dot"></span>${text}</span>${kind}`;
}
